import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaArrowRight,
  FaCalendarAlt,
  FaCheck,
  FaChevronDown,
  FaClock,
  FaEnvelope,
  FaExclamationTriangle,
  FaFileAlt,
  FaHeadset,
  FaInfoCircle,
  FaLink,
  FaShieldAlt,
  FaTimes,
} from "react-icons/fa";
import B2BHubHomeHeader from "../../components/B2BHubHomeHeader";
import HubHomeFooter from "../../components/hub/HubHomeFooter";
import DomainHeroVisual from "../../components/domain/DomainHeroVisual";
import { fetchPublicCmsPage } from "../../services/cms.service";
import { buildDomainPageModel } from "../../utils/domainPageCms";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../assets/styles/home.scss";
import "../../assets/styles/domain_redirection_policy.scss";

const SECTION_ICONS = {
  file: FaFileAlt,
  info: FaInfoCircle,
  link: FaLink,
  warning: FaExclamationTriangle,
  headset: FaHeadset,
};

function formatBadgeDate(value) {
  if (!value) return null;
  try {
    return new Date(value).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return null;
  }
}

function fallbackTodayLabel() {
  return new Date().toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function getStickyHeaderOffset() {
  const header = document.querySelector(".hub-home-header");
  return (header?.getBoundingClientRect().height || 72) + 16;
}

function scrollElementIntoViewWithOffset(el) {
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY - getStickyHeaderOffset();
  window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
}

export default function DomainRedirectionPolicy() {
  const [page, setPage] = useState(null);
  const [openIds, setOpenIds] = useState(() => new Set(["overview", "allowed-prohibited"]));
  const [activeNavId, setActiveNavId] = useState("overview");
  const pendingScrollIdRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchPublicCmsPage("DOMAIN_REDIRECTION_POLICY");
        if (!cancelled) setPage(data);
      } catch {
        if (!cancelled) setPage(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const model = useMemo(() => buildDomainPageModel(page), [page]);
  const lastUpdated = formatBadgeDate(model.updatedAt) || fallbackTodayLabel();
  const effectiveDate = formatBadgeDate(model.effectiveDate) || lastUpdated;

  useEffect(() => {
    const ids = model.sections.map((s) => s.id);
    if (!ids.length) {
      setOpenIds(new Set());
      setActiveNavId(null);
      return;
    }
    setOpenIds(new Set(ids));
    setActiveNavId((prev) => (ids.includes(prev) ? prev : ids[0]));
  }, [model.sections]);

  useEffect(() => {
    const id = pendingScrollIdRef.current;
    if (!id || !openIds.has(id)) return;
    pendingScrollIdRef.current = null;
    const run = () => {
      scrollElementIntoViewWithOffset(document.getElementById(`domain-section-${id}`));
    };
    requestAnimationFrame(() => requestAnimationFrame(run));
  }, [openIds]);

  const toggleSection = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setActiveNavId(id);
  };

  const scrollToSection = (id) => {
    setActiveNavId(id);
    setOpenIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    pendingScrollIdRef.current = id;
    if (openIds.has(id)) {
      pendingScrollIdRef.current = null;
      scrollElementIntoViewWithOffset(document.getElementById(`domain-section-${id}`));
    }
  };

  return (
    <div className="hub-domain-page">
      <B2BHubHomeHeader />

      <main className="hub-domain-page__main">
        <section className="hub-domain-hero" aria-labelledby="hub-domain-title">
          <div className="container-fluid hub-domain-hero__inner">
            <div className="hub-domain-hero__copy">
              <nav className="hub-domain-breadcrumb" aria-label="Breadcrumb">
                <Link to="/home">Home</Link>
                <span aria-hidden>&gt;</span>
                <span>Policies</span>
                <span aria-hidden>&gt;</span>
                <span aria-current="page">Domain Redirection Policy</span>
              </nav>

              <h1 id="hub-domain-title" className="hub-domain-hero__title">
                {model.title}
              </h1>
              <p className="hub-domain-hero__lead">{model.intro}</p>

              <div className="hub-domain-hero__meta">
                <span className="hub-domain-hero__meta-item">
                  <FaCalendarAlt aria-hidden />
                  <span>
                    <strong>Effective Date:</strong> {effectiveDate}
                  </span>
                </span>
                <span className="hub-domain-hero__meta-sep" aria-hidden>
                  |
                </span>
                <span className="hub-domain-hero__meta-item">
                  <FaClock aria-hidden />
                  <span>
                    <strong>Last Updated:</strong> {lastUpdated}
                  </span>
                </span>
              </div>
            </div>

            <DomainHeroVisual imageUrl={model.heroBackgroundUrl} />
          </div>
        </section>

        <section className="hub-domain-body">
          <div className="container-fluid hub-domain-body__inner">
            <aside className="hub-domain-toc" aria-label="On this page">
              <h2 className="hub-domain-toc__heading">On this page</h2>
              <ol className="hub-domain-toc__list">
                {model.sections.map((section, index) => (
                  <li key={section.id}>
                    <button
                      type="button"
                      className={`hub-domain-toc__link${
                        activeNavId === section.id ? " is-active" : ""
                      }`}
                      onClick={() => scrollToSection(section.id)}
                    >
                      <span className="hub-domain-toc__num">{index + 1}.</span>
                      {section.title}
                    </button>
                  </li>
                ))}
              </ol>
            </aside>

            <div className="hub-domain-accordion" role="list">
              {model.sections.map((section, index) => {
                const Icon = SECTION_ICONS[section.icon] || FaFileAlt;
                const isOpen = openIds.has(section.id);
                const isComparison = section.layout === "comparison";
                const isHelp = section.layout === "help";

                return (
                  <article
                    key={section.id}
                    id={`domain-section-${section.id}`}
                    className={`hub-domain-card${isOpen ? " is-open" : ""}${
                      isHelp ? " hub-domain-card--help" : ""
                    }`}
                    role="listitem"
                  >
                    <button
                      type="button"
                      className="hub-domain-card__trigger"
                      aria-expanded={isOpen}
                      aria-controls={`domain-panel-${section.id}`}
                      onClick={() => toggleSection(section.id)}
                    >
                      <span className="hub-domain-card__icon" aria-hidden>
                        <Icon />
                      </span>
                      <span className="hub-domain-card__title">
                        {index + 1}. {section.title}
                      </span>
                      <FaChevronDown className="hub-domain-card__chevron" aria-hidden />
                    </button>

                    <div
                      id={`domain-panel-${section.id}`}
                      className="hub-domain-card__panel"
                      hidden={!isOpen}
                    >
                      {isComparison ? (
                        <div className="hub-domain-card__panel-inner">
                          <div
                            className="hub-domain-card__body cms-rendered-html"
                            dangerouslySetInnerHTML={{ __html: section.body }}
                          />
                          <div className="hub-domain-compare">
                            <div className="hub-domain-compare__col hub-domain-compare__col--allowed">
                              <div className="hub-domain-compare__head">
                                <span className="hub-domain-compare__badge" aria-hidden>
                                  <FaCheck />
                                </span>
                                <h3 className="hub-domain-compare__title">
                                  {section.allowedTitle || "Allowed Redirections"}
                                </h3>
                              </div>
                              <ul className="hub-domain-compare__list">
                                {(section.allowedItems || []).map((item) => (
                                  <li key={item}>{item}</li>
                                ))}
                              </ul>
                            </div>
                            <div className="hub-domain-compare__col hub-domain-compare__col--prohibited">
                              <div className="hub-domain-compare__head">
                                <span className="hub-domain-compare__badge" aria-hidden>
                                  <FaTimes />
                                </span>
                                <h3 className="hub-domain-compare__title">
                                  {section.prohibitedTitle || "Prohibited Redirections"}
                                </h3>
                              </div>
                              <ul className="hub-domain-compare__list">
                                {(section.prohibitedItems || []).map((item) => (
                                  <li key={item}>{item}</li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      ) : isHelp ? (
                        <div className="hub-domain-card__panel-inner hub-domain-card__panel-inner--help">
                          <div
                            className="hub-domain-card__body cms-rendered-html"
                            dangerouslySetInnerHTML={{ __html: section.body }}
                          />
                          <div className="hub-domain-help-actions">
                            {section.supportEmail ? (
                              <a
                                href={`mailto:${section.supportEmail}`}
                                className="hub-domain-help-actions__email"
                              >
                                <FaEnvelope aria-hidden />
                                {section.supportEmail}
                              </a>
                            ) : null}
                            <Link
                              to={section.contactButtonLink || "/contact"}
                              className="hub-domain-help-actions__btn"
                            >
                              {section.contactButtonLabel || "Contact Support"}
                              <FaArrowRight aria-hidden />
                            </Link>
                          </div>
                        </div>
                      ) : (
                        <div className="hub-domain-card__panel-inner">
                          <div
                            className="hub-domain-card__body cms-rendered-html"
                            dangerouslySetInnerHTML={{ __html: section.body }}
                          />
                        </div>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="hub-domain-cta" aria-label="Domain policy commitment">
          <div className="container-fluid hub-domain-cta__inner">
            <div className="hub-domain-cta__banner">
              <span className="hub-domain-cta__icon" aria-hidden>
                <FaShieldAlt />
              </span>
              <div className="hub-domain-cta__text">
                <p className="hub-domain-cta__title">{model.cta.title}</p>
                <p className="hub-domain-cta__sub">{model.cta.subtitle}</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <HubHomeFooter />
    </div>
  );
}
